import { noop, debounce } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { white, navy, grey25, grey75, yellow } from '@zocdoc/branding/colors';
import { INTERACTION_TYPES, EVENT_INITIATORS } from '@zocdoc/frontend-metrics';

import { NON_INPUT_X_AFFORDANCE_MOBILE } from '../../../../picker-constants';

import { breakpoint } from '../../../../grid';
import { FontFamily } from '../../../../typography';
import { getPlaceholder } from '../../../helpers/textbox-helpers';
import { Button } from '../../../../button';
import Close from '../../../../icons/Close';
import blueIcon from '../../../images/icon-blue-copy.svg';
import whiteIcon from '../../../images/icon-white-copy.svg';
import { disableAllInputs } from './disableAll';

@disableAllInputs()
class BaseMobileHeaderView extends Component {
    static propTypes = {
        closeDropdown: PropTypes.func,
        handleAutocompleteChange: PropTypes.func.isRequired,
        markOpenDropdown: PropTypes.func,
        openScanCard: PropTypes.func,
        recordScanCardAvailable: PropTypes.func,
        shouldShowScanInsuranceCardButton: PropTypes.bool,
        textBoxChange: PropTypes.func,
        sendMetric: PropTypes.func,
    };

    static defaultProps = {
        closeDropdown: noop,
        markOpenDropdown: noop,
        openScanCard: noop,
        recordScanCardAvailable: noop,
        shouldShowScanInsuranceCardButton: false,
        textBoxChange: noop,
        sendMetric: noop,
    };

    constructor(props) {
        super(props);
        this.debouncedSendMetric = debounce(this.props.sendMetric, 500).bind(this);
    }

    render() {
        const {
            typedValue, tab, shouldShowScanInsuranceCardButton, intl,
        } = this.props;

        return (
            <MobileDropdownHeader
                ref="header"
                data-test="mobile-header"
            >
                {this.maybeRenderScanCardButton(shouldShowScanInsuranceCardButton)}

                <MobileDropdownLabel data-test="mobile-header-label">
                    {shouldShowScanInsuranceCardButton ? (
                        <FormattedMessage
                            defaultMessage="Choose manually"
                            id="Components.InsurancePicker.MobileHeader.Insurance.Manual"
                        />
                    ) : (
                        <FormattedMessage
                            defaultMessage="Choose an insurance"
                            id="Components.InsurancePicker.MobileHeader.Insurance.Choose"
                        />
                    )}
                    <StyledClose
                        height={12}
                        onClick={this.props.closeDropdown}
                    />
                </MobileDropdownLabel>
                <MobileTextBoxContainer>
                    <MobileTextbox
                        autoCapitalize="off"
                        autoComplete="off"
                        autoCorrect="off"
                        data-test="mobile-picker-textbox"
                        data-uem-id="insurance-picker-textbox"
                        innerRef={node => {
                            this.mobileTextBox = node;
                        }}
                        name={null}
                        onChange={this.handleChange}
                        placeholder={getPlaceholder(tab, intl)}
                        spellCheck="false"
                        type="text"
                        value={typedValue}
                    />
                </MobileTextBoxContainer>
            </MobileDropdownHeader>
        );
    }

    maybeRenderScanCardButton(shouldShowScanInsuranceCardButton) {
        if (shouldShowScanInsuranceCardButton === true) {
            return (
                <DropdownScanCardContainer data-test="mobile-dropdown-scan-card-container">
                    <DropdownScanCardButton
                        data-test="mobile-dropdown-scan-card-button"
                        data-uem-id="mobile-dropdown-scan-card-button"
                        fluid
                        onClick={this.handleClickScanCardButton}
                        style={Button.STYLE.SECONDARY}
                        type="button"
                    >
                        <DropdownScanCardText>
                            <FormattedMessage
                                defaultMessage="Scan your insurance card"
                                id="Components.InsurancePicker.MobileHeader.Insurance.ScanCard"
                            />
                        </DropdownScanCardText>
                        <DropdownScanCardSubtext>
                            <FormattedMessage
                                defaultMessage="Automatically detect your plan name"
                                id="Components.InsurancePicker.MobileHeader.Insurance.ScanCard.Detect"
                            />
                        </DropdownScanCardSubtext>
                        <DropdownScanCardIcon />
                    </DropdownScanCardButton>
                    <DropdownOrContainer>
                        <DropdownScanCardOrLine />
                        <DropdownScanCardOrText>
                            <FormattedMessage
                                defaultMessage="or"
                                id="Components.InsurancePicker.MobileHeader.Insurance.ScanCard.Or"
                            />
                        </DropdownScanCardOrText>
                    </DropdownOrContainer>
                </DropdownScanCardContainer>
            );
        }
    }

    componentDidMount() {
        const { isOpening, markOpenDropdown } = this.props;

        if (!isOpening) {
            return;
        }

        if (this.props.isMobileBreakpoint()) {
            this.props.setDisabled({
                disabled: true,
                ignoredElement: this.mobileTextBox,
            });

            if (this.props.shouldShowScanInsuranceCardButton) {
                this.props.recordScanCardAvailable();
                this.props.sendMetric({
                    element: 'ScanCardAvailability',
                    action: 'Insurance Picker Scan - Scan Card Available',
                    initiator: EVENT_INITIATORS.SYSTEM,
                    interactionType: INTERACTION_TYPES.VIEW,
                });
            }
        }

        markOpenDropdown();

        // Closes the branch banner/journey from the homepage when entering the insurance picker
        if (typeof window !== 'undefined' && window.branch && window.branch.closeJourney) {
            // Only close the banner if its android [for now]
            if (navigator.userAgent && navigator.userAgent.toLowerCase().indexOf('android') > -1) {
                window.branch.closeJourney();
            }
        }
    }

    componentWillUnmount() {
        this.props.setDisabled({
            disabled: false,
            ignoredElement: this.mobileTextBox,
        });
    }

    handleClickScanCardButton = e => {
        this.props.openScanCard(e);
        this.props.sendMetric({
            element: 'ScanYourInsuranceCardButton',
            action: 'Insurance Picker Scan - Click Scan Card',
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.CLICK,
        });
    };

    handleChange = e => {
        const { value } = e.target;
        this.props.handleAutocompleteChange(value);
        this.props.textBoxChange(e);
        this.debouncedSendMetric({
            action: 'Insurance Picker - Searched',
            element: 'TextInput',
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.TYPE,
            eventDetails: {
                Query: value,
            },
        });
    };
}

const MobileDropdownHeader = styled.div`
    font: 15px/24px ${FontFamily.SharpSansSemibold};
    text-align: center;
    position: relative;
    width: 100%;
    box-sizing: border-box;

    ${breakpoint.small} {
        display: none;
    }
`;

const MobileDropdownLabel = styled.div`
    padding: 8px 12px;
    color: ${navy};
`;

const StyledClose = styled(Close)`
    cursor: pointer;
    position: absolute;
    right: 12px;
    top: 8px;
    margin: 6px;
`;

const MobileTextBoxContainer = styled.div`
    padding: 0 ${NON_INPUT_X_AFFORDANCE_MOBILE}px;
`;

const MobileTextbox = styled.input`
    font: 15px/31px ${FontFamily.SharpSansSemibold};
    border: 1px solid ${navy};
    height: 40px;
    padding: 0 0 0 12px;
    margin-bottom: 12px;
    width: 100%;
    border-radius: 3px;
    color: ${navy};
    box-sizing: border-box;

    &::placeholder {
        color: ${grey75};

        &:focus {
            color: ${grey25};
        }
    }
`;

const DropdownScanCardContainer = styled.div`
    padding: 43px ${NON_INPUT_X_AFFORDANCE_MOBILE}px 0;
`;

const DropdownScanCardButton = styled(Button)`
    background-color: ${white};
    border-color: ${navy};
    border-radius: 5px;
    padding-top: 31px;

    // Remove hover styling to fix bug on phone
    //  where box remains navy even when not tapped
    &:hover {
        background-color: inherit;
        color: inherit;
    }

    &:active {
        background-color: ${navy};
        color: ${white};
    }
`;

const DropdownScanCardText = styled.div`
    font: 18px/19px ${FontFamily.SharpSansSemibold};
    color: inherit;
    height: 19px;
    margin-bottom: 8px;
`;

const DropdownScanCardSubtext = styled.div`
    font: 13px/13px ${FontFamily.SharpSansSemibold};
    color: inherit;
    height: 13px;
    margin-bottom: 23px;
`;

const DropdownScanCardIcon = styled.div`
    background: url(${blueIcon}) center no-repeat;
    margin: auto;
    pointer-events: none;
    height: 64px;
    width: 64px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${yellow};
    margin-bottom: 24px;

    &:active {
        background: url(${whiteIcon}) center no-repeat;
        border: 2px solid ${white};
    }
`;

const DropdownOrContainer = styled.div`
    height: 11px;
`;

const DropdownScanCardOrLine = styled.div`
    border-bottom: 1px solid ${grey25};
    margin-top: 26px;
`;

const DropdownScanCardOrText = styled.div`
    background-color: ${white};
    color: ${grey75};
    margin: auto;
    text-align: center;
    top: -13px;
    position: relative;
    display: inline;
    padding: 0 4px;
    right: 6px;
`;

export const MobileHeaderView = injectIntl(BaseMobileHeaderView);
